/**
 * AlertNotificationStack — Renders real-time alert notifications as stacked toasts.
 *
 * Displays WebSocket-driven alert notifications (triggered/resolved) as
 * Material UI Snackbar/Alert components stacked in the bottom-right corner.
 *
 * Features:
 * - Stacked layout with vertical offset for multiple notifications
 * - Color-coded by severity (critical = red, warning = orange, resolved = green)
 * - Shows metric name, value, and threshold for triggered alerts
 * - Manual dismiss via close button
 * - Smooth enter/exit transitions
 * - Accessible with proper ARIA roles and labels
 *
 * Validates: Task 25.5 — Build real-time alert notifications via WebSocket
 */

import { Alert, Box, Grow, IconButton, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { AlertNotification } from '../../hooks/useAlertNotifications';
import { colors } from '../../theme';

// ─── Types ──────────────────────────────────────────────────────────────────

interface AlertNotificationStackProps {
  notifications: AlertNotification[];
  onDismiss: (id: string) => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatMetricName(name: string): string {
  // Convert snake_case or camelCase to readable format
  return name
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatMetricValue(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toFixed(1);
}

function getNotificationColor(notification: AlertNotification): string {
  if (notification.type === 'resolved') {
    return colors.accent.green;
  }
  if (notification.severity === 'critical') {
    return colors.accent.red;
  }
  return colors.accent.orange;
}

function getNotificationIcon(notification: AlertNotification) {
  if (notification.type === 'resolved') {
    return <CheckCircleIcon sx={{ fontSize: 20 }} />;
  }
  if (notification.severity === 'critical') {
    return <ErrorIcon sx={{ fontSize: 20 }} />;
  }
  return <WarningAmberIcon sx={{ fontSize: 20 }} />;
}

function getNotificationTitle(notification: AlertNotification): string {
  if (notification.type === 'resolved') {
    return 'Alert Resolved';
  }
  if (notification.severity === 'critical') {
    return 'Critical Alert';
  }
  return 'Warning Alert';
}

function getNotificationMessage(notification: AlertNotification): string {
  if (notification.type === 'resolved') {
    return `Alert on server has been resolved.`;
  }

  const metric = formatMetricName(notification.metricName);
  const value = notification.metricValue != null ? formatMetricValue(notification.metricValue) : '?';
  const threshold =
    notification.thresholdValue != null ? formatMetricValue(notification.thresholdValue) : '?';

  return `${metric} is at ${value} (threshold: ${threshold})`;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function AlertNotificationStack({
  notifications,
  onDismiss,
}: AlertNotificationStackProps) {
  if (notifications.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1400, // Above MUI Drawer (1200) and AppBar (1100)
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 1,
        maxWidth: 400,
        width: '100%',
        pointerEvents: 'none', // Allow clicks to pass through the container
      }}
      role="region"
      aria-label="Alert notifications"
      aria-live="polite"
    >
      {notifications.map((notification) => {
        const color = getNotificationColor(notification);
        const icon = getNotificationIcon(notification);
        const title = getNotificationTitle(notification);
        const message = getNotificationMessage(notification);

        return (
          <Grow key={notification.id} in={notification.open} timeout={300}>
            <Paper
              elevation={8}
              sx={{
                pointerEvents: 'auto', // Re-enable clicks on the notification itself
                backgroundColor: colors.background.secondary,
                border: `1px solid ${color}`,
                borderLeft: `4px solid ${color}`,
                borderRadius: 1.5,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: color,
                  boxShadow: `0 4px 20px ${color}33`,
                },
              }}
              role="alert"
              aria-atomic="true"
            >
              <Alert
                severity={
                  notification.type === 'resolved'
                    ? 'success'
                    : notification.severity === 'critical'
                      ? 'error'
                      : 'warning'
                }
                icon={icon}
                action={
                  <IconButton
                    aria-label="Dismiss notification"
                    color="inherit"
                    size="small"
                    onClick={() => onDismiss(notification.id)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.text.primary,
                  '& .MuiAlert-icon': {
                    color,
                    alignItems: 'center',
                  },
                  '& .MuiAlert-action': {
                    alignItems: 'flex-start',
                    pt: 0.5,
                  },
                  py: 1,
                  px: 1.5,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color, lineHeight: 1.3 }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: colors.text.secondary, mt: 0.25, lineHeight: 1.4 }}
                >
                  {message}
                </Typography>
                {notification.serverId && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.text.secondary,
                      opacity: 0.7,
                      mt: 0.5,
                      display: 'block',
                      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                      fontSize: '0.7rem',
                    }}
                  >
                    Server: {notification.serverId.slice(0, 8)}…
                  </Typography>
                )}
              </Alert>
            </Paper>
          </Grow>
        );
      })}
    </Box>
  );
}
